import { isBefore, addDays } from 'date-fns';
import { isEmpty } from 'lodash';
import isIP from 'validator/lib/isIP';
import isFQDN from 'validator/lib/isFQDN';
import isURL from 'validator/lib/isURL';
import isHash from 'validator/lib/isHash';

import {
  EXPIRATION,
  IOC_TYPE_TO_FEED_TYPE,
  IOC_TYPES,
  IOC_TYPES_NEW,
  IOC_UI_STATUS,
  IOC_TYPE_TO_TEM_IOC_TYPE
} from '../constants/iocTypes';

export const getIOCType = query => {
  let type = null;

  if (isIP(query)) {
    type = IOC_TYPES.IPV4;
  } else if (isFQDN(query)) {
    type = IOC_TYPES.DOMAIN;
  } else if (isURL(query)) {
    type = IOC_TYPES.URL;
  } else if (isHash(query, 'sha256')) {
    type = IOC_TYPES.HASH_SHA_256;
  }
  return type;
};

export const getIOCTypeNew = query => {
  if (!query) return null;
  if (isIP(query, 4)) {
    return IOC_TYPES_NEW.IPV4;
  }
  if (isIP(query, 6)) {
    return IOC_TYPES_NEW.IPV6;
  }
  if (isFQDN(query)) {
    return IOC_TYPES_NEW.DOMAIN;
  }
  if (isURL(query)) {
    return IOC_TYPES_NEW.URL;
  }
  if (isHash(query, 'sha256')) {
    return IOC_TYPES_NEW.HASH_SHA_256;
  }
  if (isHash(query, 'sha1')) {
    return IOC_TYPES_NEW.SHA1;
  }
  if (isHash(query, 'md5')) {
    return IOC_TYPES_NEW.MD5;
  }
  return null;
};

export const getFeedIocType = ({ ermIocType }) => {
  return IOC_TYPE_TO_FEED_TYPE[ermIocType] || null;
};

const getObservationKey = ({ type, value }) => `${type}::${value}`;
const IOC_SOURCE = {
  CHECK_POINT: 'checkPoint',
  EXTERNAL: 'external'
};

export const buildFeedRequestPayload = ({ iocs = [], maxSize = 50 }) => {
  const payloadMap = new Map();

  for (const ioc of iocs) {
    if (payloadMap.size >= maxSize) {
      break;
    }

    const value = ioc?.observable_value;
    const type = getFeedIocType({ ermIocType: ioc?.entity_type });

    if (!ioc?.enriched && type && value) {
      const key = getObservationKey({ type, value });

      if (!payloadMap.has(key)) {
        payloadMap.set(key, { type, value });
      }
    }
  }

  return Array.from(payloadMap.values());
};

export const enrichIocsWithFeedObservations = ({ iocs = [], allIocs = [], observations = [], requestedKeys }) => {
  const observationMap = new Map(
    (observations || []).map(observation => [
      getObservationKey({ type: observation.type, value: observation.value }),
      observation
    ])
  );

  const applyFeedObservations = iocsList =>
    (iocsList || []).map(ioc => {
      if (ioc?.enriched) {
        return ioc;
      }

      const type = getFeedIocType({ ermIocType: ioc?.entity_type });
      const value = ioc?.observable_value;
      const key = type && value ? getObservationKey({ type, value }) : null;

      // When a batch is supplied, only enrich/flag the iocs that were actually requested; the rest
      // stay unenriched so the next scroll batch picks them up.
      if (requestedKeys && (!key || !requestedKeys.has(key))) {
        return ioc;
      }

      const fallbackLastSeen = ioc?.last_seen || ioc?.updated_at || null;
      const sourceFromOriginalIoc = ioc?.last_seen ? IOC_SOURCE.CHECK_POINT : IOC_SOURCE.EXTERNAL;

      if (!type || !value) {
        return {
          ...ioc,
          last_seen: fallbackLastSeen,
          source: sourceFromOriginalIoc,
          enriched: true
        };
      }

      const observation = observationMap.get(key);
      const sourceFromFeedOrOriginal = observation?.last_seen ? IOC_SOURCE.CHECK_POINT : sourceFromOriginalIoc;

      return {
        ...ioc,
        last_seen: observation?.last_seen || fallbackLastSeen,
        source: sourceFromFeedOrOriginal,
        enriched: true
      };
    });

  return {
    iocs: applyFeedObservations(iocs),
    allIocs: applyFeedObservations(allIocs)
  };
};

const IOC_RISK_STATUS_MAP = {
  RISKY: {
    icon: 'var(--severityRed, #ff241c)',
    color: 'var(--severityRed, #ff241c)',
    backgroundColor: 'var(--mistyRose, #f6e1e0)',
    label: 'Malicious',
    value: IOC_UI_STATUS.MALICIOUS
  },
  BENIGN: {
    icon: 'var(--kellyGreen, #00AD11)',
    color: 'var(--officeGreen, #187F16)',
    backgroundColor: 'var(--nyanza, #e6ffe4)',
    label: 'Benign',
    value: IOC_UI_STATUS.BENIGN
  },
  NEUTRAL: {
    icon: 'var(--darkGoldenrod, #b98836)',
    color: 'var(--darkGoldenrod, #b98836)',
    backgroundColor: 'var(--cornsilk, #FFF7D8)',
    label: 'Inconclusive',
    value: IOC_UI_STATUS.INCONCLUSIVE
  },
  DEFAULT: {
    icon: 'var(--darkSlateGray3, #475b5e)',
    color: 'var(--darkSlateGray3, #475b5e)',
    backgroundColor: 'var(--platinum2, #DEE6E7)',
    label: '',
    value: ''
  }
};

export const getIOCStatusNew = iocDetails => {
  const maliciousStatus = iocDetails?.top_activity?.malicious;
  if (!iocDetails || !maliciousStatus) return IOC_RISK_STATUS_MAP.DEFAULT;

  switch (maliciousStatus) {
    case 'yes':
      return IOC_RISK_STATUS_MAP.RISKY;
    case 'no':
      return IOC_RISK_STATUS_MAP.BENIGN;
    case 'inconclusive':
      return IOC_RISK_STATUS_MAP.NEUTRAL;
    default:
      return IOC_RISK_STATUS_MAP.DEFAULT;
  }
};

export const getIOCStatusDetails = iocDetails => {
  const { detected_activities } = iocDetails?.risk || {};

  if (!iocDetails) return IOC_RISK_STATUS_MAP.DEFAULT;

  const isRisky = detected_activities?.length > 0;
  const isBenign = iocDetails?.benign;
  let riskStatus;
  // Determine the status
  if (isRisky) {
    riskStatus = IOC_RISK_STATUS_MAP.RISKY;
  } else if (isBenign) {
    riskStatus = IOC_RISK_STATUS_MAP.BENIGN;
  } else {
    riskStatus = IOC_RISK_STATUS_MAP.NEUTRAL;
  }
  return riskStatus;
};

/**
 * Determine if IOC is not expired based on entity type and observation date:
 * - sha256: Last Observation date > 1 year
 * - domain & url: Last Observation date > 1 week
 * - ipv4: Last Observation date > 1 day
 */
const hashTypes = [
  IOC_TYPES.HASH_SHA_256,
  IOC_TYPES_NEW.HASH_SHA_256,
  IOC_TYPES.SHA_256,
  IOC_TYPES_NEW.SHA1,
  IOC_TYPES_NEW.MD5
].map(t => t.toLowerCase());
const urlTypes = [IOC_TYPES.DOMAIN, IOC_TYPES.DOMAIN_NAME, IOC_TYPES.URL].map(t => t.toLowerCase());
const ipTypes = [IOC_TYPES.IPV4, IOC_TYPES.IPV4_ADDR].map(t => t.toLowerCase());
export const getIocExpirationDate = ioc => {
  if (!ioc?.entity_type || !ioc?.updated_at) return null;

  const observationDate = new Date(ioc.updated_at);
  const iocType = ioc.entity_type.toLowerCase();
  if (hashTypes.includes(iocType)) return addDays(observationDate, EXPIRATION[IOC_TYPES.HASH_SHA_256]);
  if (urlTypes.includes(iocType)) return addDays(observationDate, EXPIRATION[IOC_TYPES.DOMAIN]);
  if (ipTypes.includes(iocType)) return addDays(observationDate, EXPIRATION[IOC_TYPES.IPV4]);
  return null;
};

export const getIocExpirationDetails = ioc => {
  const expiration = getIocExpirationDate(ioc);
  return { expiration, isExpired: expiration ? isBefore(expiration, new Date()) : null };
};

export const isIocExpired = ioc => getIocExpirationDetails(ioc)?.isExpired;

export const getTemType = ({ ermIocType, ermIocValue }) => {
  return IOC_TYPE_TO_TEM_IOC_TYPE[ermIocType] || IOC_TYPE_TO_TEM_IOC_TYPE[getIOCTypeNew(ermIocValue)];
};

export const isTemTypeSupported = ({ ermIocType, ermIocValue, temSupportedTypes }) => {
  if (isEmpty(temSupportedTypes)) return true;
  const temType = getTemType({ ermIocType, ermIocValue });
  return temType ? temSupportedTypes.includes(temType) : false;
};

export const getIocId = ioc => (typeof ioc === 'string' ? ioc : ioc?.observable_value || ioc?.value || ioc?.id);
